import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import GPTChat from './GPTChat'; 
import SourdoughInput from './SourdoughInput';
import SourdoughRecipeCard from './SourdoughRecipeCard';
import './SourdoughCalculator.css';
import smallicon from './smallicon.png';

//
// Utility functions
//
const roundToOneDecimal = (val) => Math.round(val * 10) / 10;
const formatTime = (hours) => {
  const hrs = Math.floor(hours);
  const mins = Math.round((hours - hrs) * 60);
  return `${hrs} hrs ${mins} mins`;
};

export default function SourdoughCalculator({ doughData, setDoughData }) {
  const {
    flour,
    protein,
    starter,
    starterHydration,
    doughHydration,
    isFahrenheit,
    temperature,
    notes = ''
  } = doughData;

  // GPT recipe result + loading
  const [recipe, setRecipe] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);

  // Dialogs (crumb + chat)
  const [openCrumb, setOpenCrumb] = useState(false);
  const [openChat, setOpenChat] = useState(false);

  // MUI Theme
  const theme = createTheme({
    palette: {
      primary: { main: '#8B4513' }
    }
  });

  // Helper to convert temp
  const convertTemperature = (temp, toF) => {
    return toF
      ? Math.round((temp * 9) / 5 + 32)
      : Math.round(((temp - 32) * 5) / 9);
  };

  // Master setField
  const setField = (field, value) => {
    setDoughData((prev) => ({ ...prev, [field]: value }));
  };

  // Toggle between °C and °F
  const onToggleTemperature = () => {
    setDoughData((prev) => {
      const tempVal = prev.temperature === '' ? 25 : parseFloat(prev.temperature);
      const newTemp = convertTemperature(tempVal, !prev.isFahrenheit).toString();
      return {
        ...prev,
        temperature: newTemp,
        isFahrenheit: !prev.isFahrenheit
      };
    });
  };

  // Calculate recipe
  const calculateRecipe = () => {
    const mainFlour = parseFloat(flour) || 0;
    const proteinPct = parseFloat(protein) || 12;
    const starterGrams = parseFloat(starter) || 0;
    const starterHydro = parseFloat(starterHydration) || 100;
    const desiredHydro = parseFloat(doughHydration) || 70;

    const tempC = isFahrenheit
      ? ((parseFloat(temperature) - 32) * 5) / 9
      : parseFloat(temperature);

    const flourInStarter = starterGrams / (1 + starterHydro / 100);
    const waterInStarter = starterGrams - flourInStarter;
    const totalFlour = mainFlour + flourInStarter;
    const totalWaterDesired = totalFlour * (desiredHydro / 100);
    const additionalWaterNeeded = totalWaterDesired - waterInStarter;
    const salt = totalFlour * 0.0237;

    // Estimate bulk fermentation time
    let bfTime = 4.0;
    bfTime += (25 - tempC) * 0.3;
    bfTime += (100 - starterGrams) * 0.01;
    bfTime += (70 - desiredHydro) * 0.05;
    bfTime -= (proteinPct - 12) * 0.05;
    bfTime = Math.max(2, Math.min(24, bfTime));

    setDoughData((prev) => ({
      ...prev,
      totalFlourOutput: roundToOneDecimal(mainFlour),
      waterOutput: roundToOneDecimal(additionalWaterNeeded),
      starterOutput: roundToOneDecimal(starterGrams),
      saltOutput: roundToOneDecimal(salt),
      bulkFermentationTime: roundToOneDecimal(bfTime),
      coldProofTime: 12
    }));
  };

  // Fetch GPT recipe
  const fetchRecipe = async (finalData) => {
    try {
      setLoadingRecipe(true);
      setRecipe(null);

      const openaiApiKey = process.env.DOMIGO_OPENAI_API_KEY;  

      const systemMessage = {
        role: 'system',
        content: `You are a helpful assistant that generates sourdough recipe instructions in a very specific JSON-like format. The recipe should reflect different styles, flour types, and fermentation techniques as per the user's inputs.`
      };

      const userMessage = {
        role: 'user',
        content: `
        Return a sourdough recipe in the EXACT following format (JSON-like, no extra keys), based on these parameters:

        Autolyse: text
        Starter: text
        Stretch & Folds: text
        Bulk Fermentation: ${formatTime(finalData.bulkFermentationTime)}
        Shaping: text
        Cold proofing: text
        Bake: text
        domigo's Advice: text

        Where:
        - "Bulk Fermentation" MUST be "${formatTime(finalData.bulkFermentationTime)}"
        - Temperatures in "${finalData.isFahrenheit ? 'Fahrenheit' : 'Celsius'}" 
        - flour: ${finalData.totalFlourOutput} g
        - water: ${finalData.waterOutput} g
        - starter: ${finalData.starterOutput} g
        - salt: ${finalData.saltOutput} g
        - temperature scale: ${finalData.isFahrenheit ? 'Fahrenheit' : 'Celsius'}
        - notes: ${finalData.notes}; if not empty, these notes should adjust all of your responses.
        - You can stretch & folds and fermentation time based on flour protein and hydration.
        - Include techniques like pre-shaping or lamination if relevant.
        - The bake process should consider any notes, or do a 2-step Dutch oven bake if none provided.
        - Autolyse is always just flour and water
        - Cold proof is alsways overnight, or a value between 8 and 12 hours
        - "domigo's Advice": Offer very specific advice based on ${finalData.notes}. Give a motivational message.

        Ensure you ONLY and ALWAYS return (never bullets tables):
        1. text about autolyse
        2. text about adding starter and salt
        3. text about stretch & folds
        4. value bulk fermentation
        5. text about shaping
        6. text or value cold proof in the same "hrs/mins" format
        7. text about baking plan
        8. text about how you may adjust the recipe above based on the inputs

        (No extra keys.)
        `
      };

      // CHANGED HERE: now POST to our local /api/fetch
      const response = await fetch('/api/fetch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini-2024-07-18',
          messages: [systemMessage, userMessage],
          max_tokens: 1500,
          temperature: 0.7
        })
      });

      if (!response.ok) throw new Error('GPT API request failed.');

      const jsonResponse = await response.json();
      const gptMessage = jsonResponse?.choices?.[0]?.message?.content?.trim() || '';

      // Attempt to parse as JSON; fallback to raw string if parse fails
      let parsed;
      try {
        parsed = JSON.parse(gptMessage.replace(/^\s*```json\s*|\s*```$/g, ''));
      } catch (err) {
        parsed = gptMessage;
      }

      setRecipe(parsed);
    } catch (err) {
      console.error('Error fetching GPT recipe:', err);
      setRecipe(null);
    } finally {
      setLoadingRecipe(false);
    }
  };

  // Re-calc on input changes
  useEffect(() => {
    calculateRecipe();
    // eslint-disable-next-line
  }, [
    flour,
    protein,
    starter,
    starterHydration,
    doughHydration,
    temperature,
    isFahrenheit
  ]);

  // Pressing Enter in any field triggers GPT fetch
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      fetchRecipe({
        ...doughData,
        totalFlourOutput: doughData.totalFlourOutput,
        waterOutput: doughData.waterOutput,
        starterOutput: doughData.starterOutput,
        saltOutput: doughData.saltOutput,
        bulkFermentationTime: doughData.bulkFermentationTime,
        coldProofTime: doughData.coldProofTime,
        isFahrenheit: doughData.isFahrenheit,
        notes: doughData.notes
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div style={{  width: '100%', maxWidth: '100%', overflow: 'auto' }}>
        {/* Title */}
        {/* "domigo" Title with Bouncing Icon */}
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          mb={2}
          sx={{ marginTop: { xs: 0, sm: 2 } }}
        >
          <Typography variant="h4" sx={{ color: '#5c3c14', fontWeight: 'bold', marginRight: -1.8 }}>
            d
          </Typography>
          <img src={smallicon} alt="icon" className="bounceIcon" />
          <Typography variant="h4" sx={{ color: '#5c3c14', fontWeight: 'bold', marginLeft: -1.8  }}>
            migo
          </Typography>
        </Box>

        {/* Inputs */}
        <SourdoughInput
          flour={flour}
          protein={protein}
          starter={starter}
          starterHydration={starterHydration}
          doughHydration={doughHydration}
          temperature={temperature}
          isFahrenheit={isFahrenheit}
          notes={notes}
          setField={setField}
          onToggleTemperature={onToggleTemperature}
          handleKeyDown={handleKeyDown}
        />

        {/* Recipe Card */}
        <SourdoughRecipeCard
          doughData={doughData}
          recipe={recipe}
          loadingRecipe={loadingRecipe}
        />

        {/* Buttons row */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            sx={{ 
              mr: 2, 
              borderRadius: '20px'
            }}
            onClick={() => setOpenCrumb(true)}
          >
            check how i did
          </Button>

          <Button 
            variant="contained" 
            sx={{ 
              borderRadius: '20px'
            }} 
            onClick={() => setOpenChat(true)}
          >
            ask a question
          </Button>
        </Box>

        {/* Popout for crumb.png */}
        <Dialog
          open={openCrumb}
          onClose={() => setOpenCrumb(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: '#f7f0e7',
              borderRadius: { xs: 0, sm: '12px' },
              width: { xs: '100vw', sm: 'auto' },
              maxWidth: { xs: '100vw', sm: '800px' },
              height: { xs: 'auto', sm: 'auto' },
              maxHeight: { sm: '90vh' }
            }
          }}
          fullScreen={{ xs: true, sm: false }}
        >
          {/* Close Button for Mobile */}
          <DialogActions
            sx={{
              display: { xs: 'flex', sm: 'none' },
              position: 'absolute',
              top: 4,
              right: -22,
              zIndex: 10
            }}
          >
            <Button
              onClick={() => setOpenCrumb(false)}
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                color: '#5c3c14',
                fontSize: '18px',
                fontWeight: 'bold',
              }}
            >
              ✕
            </Button>
          </DialogActions>

          <DialogContent
            sx={{
              p: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <img
              src="/crumb.png"
              alt="crumb"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: window.innerWidth > 600 ? '12px' : '0px'
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Popout for GPTChat */}
        <Dialog
          open={openChat}
          onClose={() => setOpenChat(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={{ xs: true, sm: false }}
          BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.3)' } }}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: '#f7f0e7',
              borderRadius: { xs: 0, sm: '12px' },
              width: { xs: '100%', sm: '500px' },
              maxHeight: { sm: '80vh' }
            }
          }}
        >
          {/* Close Button for Mobile */}
          <DialogActions
            sx={{
              display: { xs: 'flex', sm: 'none' },
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10
            }}
          >
            <Button
              onClick={() => setOpenChat(false)}
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                color: '#5c3c14',
                fontSize: '18px',
                fontWeight: 'bold',
              }}
            >
              ✕
            </Button>
          </DialogActions>

          <DialogContent sx={{ p: 0, height: '100%' }}>
            <GPTChat doughData={doughData} openChat={openChat} setOpenChat={setOpenChat} />
          </DialogContent>
        </Dialog>

        {/* Footer */}
        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          sx={{ mt: 4 }}
          paddingTop={3}
          fontSize={14}
        >
          Built with ❤️ by{' '}
          <a
            href="https://www.fincuva.com/"
            style={{ color: '#D4B9DB', fontWeight: 'bold', textDecoration: 'none'}}
            target="_blank"
          >
            fincuva
          </a>
        </Typography>
        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          sx={{ mt: 1 }}
          paddingBottom={10}
        >
          Thank you to {' '}
          <a
            href="https://www.reddit.com/r/Sourdough/comments/1b0t5jr/reading_crumb_for_fermentation/"
            style={{ color: '#FF4500', fontWeight: 'bold', textDecoration: 'none' }}
            rel="noreferrer"
            target="_blank"
          >
            u/ninnima
          </a> for the crumb graphic
        </Typography>
      </div>
    </ThemeProvider>
  );
}
